// Word-level diff for the language revision modal: [{ type: 'eq' | 'del' | 'ins', text }]

const TOKEN_PATTERN = /\s+|[\p{L}\p{N}]+|[^\s\p{L}\p{N}]/gu;
const MAX_CELLS = 4_000_000;

function tokenize(text) {
  return String(text || '').match(TOKEN_PATTERN) || [];
}

function lcsOps(a, b) {
  const n = a.length;
  const m = b.length;
  if (!n && !m) return [];
  if (!n) return b.map((text) => ({ type: 'ins', text }));
  if (!m) return a.map((text) => ({ type: 'del', text }));
  if (n * m > MAX_CELLS) return [...a.map((text) => ({ type: 'del', text })), ...b.map((text) => ({ type: 'ins', text }))];

  // lengths[i][j] = LCS of a[i:] and b[j:], flattened
  const width = m + 1;
  const lengths = new Uint32Array((n + 1) * width);
  for (let i = n - 1; i >= 0; i -= 1) {
    for (let j = m - 1; j >= 0; j -= 1) {
      lengths[i * width + j] = a[i] === b[j] ? lengths[(i + 1) * width + j + 1] + 1 : Math.max(lengths[(i + 1) * width + j], lengths[i * width + j + 1]);
    }
  }
  const ops = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      ops.push({ type: 'eq', text: a[i] });
      i += 1;
      j += 1;
    } else if (lengths[(i + 1) * width + j] >= lengths[i * width + j + 1]) {
      ops.push({ type: 'del', text: a[i] });
      i += 1;
    } else {
      ops.push({ type: 'ins', text: b[j] });
      j += 1;
    }
  }
  while (i < n) ops.push({ type: 'del', text: a[i++] });
  while (j < m) ops.push({ type: 'ins', text: b[j++] });
  return ops;
}

// Merge runs and fold lone whitespace between two edits into the edit, so "a b" → "c d" reads as one replacement
function tidy(ops) {
  const folded = ops.map((op, index) => {
    if (op.type !== 'eq' || op.text.trim()) return op;
    const prev = ops[index - 1];
    const next = ops[index + 1];
    return prev && next && prev.type !== 'eq' && next.type !== 'eq' ? { type: 'both', text: op.text } : op;
  });

  const result = [];
  let dels = '';
  let ins = '';
  const flush = () => {
    if (dels) result.push({ type: 'del', text: dels });
    if (ins) result.push({ type: 'ins', text: ins });
    dels = '';
    ins = '';
  };
  for (const op of folded) {
    if (op.type === 'eq') {
      flush();
      const last = result[result.length - 1];
      if (last?.type === 'eq') last.text += op.text;
      else result.push({ ...op });
    } else if (op.type === 'del') dels += op.text;
    else if (op.type === 'ins') ins += op.text;
    else {
      dels += op.text;
      ins += op.text;
    }
  }
  flush();
  return result;
}

export function diffWords(before, after) {
  const a = tokenize(before);
  const b = tokenize(after);
  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA -= 1;
    endB -= 1;
  }
  const ops = [...a.slice(0, start).map((text) => ({ type: 'eq', text })), ...lcsOps(a.slice(start, endA), b.slice(start, endB)), ...a.slice(endA).map((text) => ({ type: 'eq', text }))];
  return tidy(ops);
}

/*
  Shorten long unchanged stretches to a little context around each edit. Returns ops where a shortened stretch becomes
  { type: 'eq', text, collapsed: true } with the ellipsis already in `text`.
*/
export function collapseUnchanged(ops, { context = 70, minCollapse = 220 } = {}) {
  return ops.map((op, index) => {
    if (op.type !== 'eq' || op.text.length < minCollapse) return op;
    const isFirst = index === 0;
    const isLast = index === ops.length - 1;
    const head = isFirst ? '' : cutAtWord(op.text.slice(0, context), 'end');
    const tail = isLast ? '' : cutAtWord(op.text.slice(-context), 'start');
    return { type: 'eq', text: [head, '…', tail].filter(Boolean).join(' '), collapsed: true };
  });
}

function cutAtWord(text, side) {
  if (side === 'end') {
    const cut = text.lastIndexOf(' ');
    return (cut > 20 ? text.slice(0, cut) : text).replace(/\s+$/, '');
  }
  const cut = text.indexOf(' ');
  return (cut >= 0 && cut < text.length - 20 ? text.slice(cut + 1) : text).replace(/^\s+/, '');
}

export function countEdits(ops) {
  return ops.filter((op) => op.type !== 'eq').length;
}
